import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { compose, branch, renderNothing, withPropsOnChange } from 'recompose';
import classnames from 'classnames';

import { Link } from 'components/core/Link';

import './Pagination.scss';

export const enhance = compose(
	branch((props) => !props.pages || props.pages.length < 2, renderNothing),
	withPropsOnChange(
		['selectedPage', 'pages'],
		({ selectedPage, pages, pageLimit = 5 }) => {
			const totalPagesLength = pages.length;
			const pageIndex = selectedPage - 1;

			const startPage = Math.max(
				1,
				selectedPage - Math.floor(pageLimit / 2),
			);

			const endPage = Math.min(
				startPage + pageLimit - 1,
				totalPagesLength,
			);

			const notAtStartOfPages = startPage !== 1;
			const notAtEndOfPages = endPage !== totalPagesLength;

			const pagesToShow = [];

			for (let i = startPage; i <= endPage; i += 1) {
				pagesToShow.push(i);
			}

			if (notAtStartOfPages) {
				pagesToShow.unshift(1);
			}

			if (endPage !== totalPagesLength) {
				pagesToShow.push(totalPagesLength);
			}

			return {
				prevPage: get(pages, pageIndex - 1, {}),
				nextPage: get(pages, pageIndex + 1, {}),
				notAtStartOfPages,
				notAtEndOfPages,
				pagesToShow,
			};
		},
	),
);

export function Pagination({
	className,
	pages,
	selectedPage,
	prevPage,
	nextPage,
	pagesToShow,
	notAtStartOfPages,
	notAtEndOfPages,
}) {
	return (
		<ul className={classnames('ss-pagination', className)}>
			<li className="pagination-item">
				<Link
					className={classnames('pagination-link', {
						disabled: selectedPage === 1,
					})}
					to={prevPage.url}
					rel={prevPage.nofollow ? 'nofollow' : null}
				>
					&laquo;
					<span className="visually-hidden">Previous page </span>
				</Link>
			</li>
			{pagesToShow &&
				pagesToShow.map((page, index) => {
					const canShowStartExpander =
						index === 1 && notAtStartOfPages;
					const canShowEndExpander =
						index === pagesToShow.length - 2 && notAtEndOfPages;

					let paginationItemContent;

					if (canShowStartExpander || canShowEndExpander) {
						paginationItemContent = '...';
					} else {
						paginationItemContent = page;
					}

					return (
						<li
							className="pagination-item"
							key={page}
						>
							<Link
								className={classnames('pagination-link', {
									active: page === selectedPage,
								})}
								to={pages[page - 1].url}
								rel={
									pages[page - 1].nofollow ? 'nofollow' : null
								}
							>
								<span className="visually-hidden">Page</span>
								{paginationItemContent}
							</Link>
						</li>
					);
				})}
			<li className="pagination-item">
				<Link
					className={classnames('pagination-link', {
						disabled: selectedPage === pages.length,
					})}
					to={nextPage.url}
					rel={nextPage.nofollow ? 'nofollow' : null}
				>
					<span className="visually-hidden">Next page </span>&raquo;
				</Link>
			</li>
		</ul>
	);
}

Pagination.propTypes = {
	className: PropTypes.string,
	pages: PropTypes.arrayOf(
		PropTypes.shape({
			url: PropTypes.string,
			label: PropTypes.number,
			nofollow: PropTypes.bool,
		}),
	).isRequired,
	prevPage: PropTypes.object.isRequired,
	nextPage: PropTypes.object.isRequired,
	selectedPage: PropTypes.number,
	notAtStartOfPages: PropTypes.bool,
	notAtEndOfPages: PropTypes.bool,
	pagesToShow: PropTypes.arrayOf(PropTypes.number),
};

export default enhance(Pagination);
